import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Input from '@/components/molecules/input/input';
import ContentSearchBarInput from '@/components/organisms/content-search-bar/content-search-input/content-search-bar-input';
import Link from '@/components/utilities/link/link';
import { useQueryContentSearchAutocomplete } from '@/integrations/content-search/content-search-autocomplete/content-search-autocomplete.hooks';
import { useContentSearchContext } from '@/libraries/contexts/content-search-context/content-search-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { highlightTerm } from '@/libraries/formatters/highlight-term';
import useClickOutside from '@/libraries/hooks/use-click-outside';
import useDebounce from '@/libraries/hooks/use-debounce';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './content-search-bar.module.scss';

export interface ContentSearchBarProps {
  /** The class name to apply */
  className?: string;
  /** Whether to automatically focus the input on mount */
  focus?: boolean;
  /** Whether the search bar is used in a dropdown or not */
  isStandAlone?: boolean;
  /** The link of the cta button */
  link?: Content.Properties.Link | null;
  /** An event that is triggered on input field focus */
  onChangeFocus?: (value: boolean) => void;
  /** An event that is triggered on search */
  onSearch?: () => void;
  /** Whether to trigger a full page load or not */
  shouldRedirect?: boolean;
}

const DEBOUNCE_DELAY = 250;
const SEARCH_INPUT_THRESHOLD = 1;
const RECENT_CONTENT_SEARCH_LIMIT = 5;

const ContentSearchBar = ({
  className,
  focus = false,
  isStandAlone = false,
  link,
  onChangeFocus,
  onSearch,
  shouldRedirect = false,
}: ContentSearchBarProps) => {
  const { t } = useI18n();
  const router = useRouter();
  const pathname = usePathname();

  const isLaptop = useWindowSize('laptop');

  const [activeIndex, setActiveIndex] = useState(-1);
  const [searchString, setSearchString] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const activeIndexRef = useRef(activeIndex);
  const inputRef = useRef<HTMLInputElement>(null);
  const contentSearchRef = useRef<HTMLDivElement>(null);
  const linkRefs = useRef<(HTMLAnchorElement | HTMLButtonElement | null)[]>([]);

  const { value: debouncedSearchString } = useDebounce(
    searchString,
    DEBOUNCE_DELAY,
  );

  useClickOutside(
    contentSearchRef,
    () => {
      setIsDropdownOpen(false);
      onChangeFocus && onChangeFocus(false);
    },
    isDropdownOpen,
  );

  const {
    recentContentSearches,
    addContentSearchInput,
    contentSearchSettings,
    trimRecentContentSearches,
    contentSearchInput,
  } = useContentSearchContext();

  const hasRedirectUrl = !!(shouldRedirect && link && pathname !== link.url);
  const redirectUrl = link && hasRedirectUrl ? link.url : undefined;

  const isAutocompleteEnabled =
    !!contentSearchSettings?.parameters &&
    !!debouncedSearchString &&
    debouncedSearchString.length >= SEARCH_INPUT_THRESHOLD;

  const { data: dataAutocomplete } = useQueryContentSearchAutocomplete(
    {
      ...contentSearchSettings?.parameters,
      searchString: debouncedSearchString,
    },
    { headers: contentSearchSettings?.headers },
    {
      enabled: isAutocompleteEnabled,
    },
  );

  const hasRecentContentSearches =
    recentContentSearches && recentContentSearches.length > 0;

  const hasPopularSearches =
    contentSearchSettings?.data?.popularSearches?.length > 0;

  const hasAutocompleteSuggestions = !!(
    searchString &&
    dataAutocomplete &&
    dataAutocomplete.length > 0
  );

  const hasDefaultSuggestions =
    !searchString && (hasPopularSearches || hasRecentContentSearches);

  const shouldShowDropdown =
    isDropdownOpen && (hasDefaultSuggestions || hasAutocompleteSuggestions);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActiveIndex(-1);
    setSearchString(event.target.value);
  };

  const handleSearch = (value: string) => {
    addContentSearchInput(value);
    setIsDropdownOpen(false);
    setSearchString(isStandAlone ? value : '');
    onSearch && onSearch();
    trimRecentContentSearches(RECENT_CONTENT_SEARCH_LIMIT);
  };

  const handleSearchButtonClick = () => {
    if (!searchString) return;

    handleSearch(searchString);
  };

  const handleDropdownItemClick = (value: string) => {
    if (!value) return;

    handleSearch(value);
  };

  const handleEnterKeyPress = () => {
    if (!searchString) return;

    if (activeIndex > -1) return;

    handleSearch(searchString);

    inputRef.current?.blur();

    if (!redirectUrl) return;

    router.push(redirectUrl);
  };

  const handleMouseMove = () => {
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const validLinks = linkRefs.current.filter(
      (ref): ref is HTMLAnchorElement => ref !== null,
    );
    const totalItems = validLinks.length;

    if (totalItems === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(prev => {
        if (prev === -1 || prev === totalItems - 1) return 0;

        return prev + 1;
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(prev => {
        if (prev === -1 || prev === 0) return totalItems - 1;

        return prev - 1;
      });
    } else if (event.key === 'Enter' && activeIndexRef.current >= 0) {
      event.preventDefault();

      inputRef.current?.blur();

      linkRefs.current[activeIndexRef.current]?.click();
    }
  };

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    if (!isStandAlone) return;

    setSearchString(contentSearchInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentSearchInput]);

  useEffect(() => {
    if (!focus) return;

    inputRef.current?.focus();
  }, [focus]);

  useEffect(() => {
    if (!contentSearchRef.current) return;

    const contentSearchElement = contentSearchRef.current;

    document.addEventListener('keydown', handleKeyDown);
    contentSearchElement.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      contentSearchElement.removeEventListener('mousemove', handleMouseMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderInputField = () => {
    if (isLaptop) {
      return (
        <ContentSearchBarInput
          ref={inputRef}
          icon="Search"
          label={t('items.common.words.search')}
          onChange={handleInputChange}
          onClick={handleSearchButtonClick}
          onEnter={handleEnterKeyPress}
          onFocus={() => {
            setIsDropdownOpen(true);
            onChangeFocus && onChangeFocus(true);
          }}
          url={redirectUrl}
          value={searchString}
        />
      );
    }

    return (
      <Input
        ref={inputRef}
        hasClear
        icon="Search"
        label={t('items.common.words.search')}
        onChange={handleInputChange}
        onClear={() => setSearchString('')}
        onEnter={handleEnterKeyPress}
        onFocus={() => {
          setIsDropdownOpen(true);
          onChangeFocus && onChangeFocus(true);
        }}
        value={searchString}
        variant="search"
      />
    );
  };

  const renderDropdownContent = () => {
    let itemIndex = 0;

    if (hasDefaultSuggestions) {
      return (
        <React.Fragment>
          {hasRecentContentSearches && (
            <div className={styles.recentSearches}>
              <Typography
                className={styles.recentSearchesHeadline}
                tagStyle="bodyLargeBold"
              >
                {t('items.contentSearch.recentSearches')}
              </Typography>

              {recentContentSearches.map((recentSearch, recentSearchIndex) => {
                const index = itemIndex++;

                return (
                  <Link
                    key={recentSearchIndex}
                    ref={el => {
                      linkRefs.current[index] = el;
                    }}
                    className={classNames(styles.link, {
                      [styles.isActive]: activeIndex === index,
                    })}
                    onClick={() => handleDropdownItemClick(recentSearch)}
                    url={redirectUrl}
                  >
                    <Typography tagStyle="bodyLarge">{recentSearch}</Typography>
                  </Link>
                );
              })}
            </div>
          )}

          {hasPopularSearches && (
            <div className={styles.popularSearches}>
              <Typography
                className={styles.popularSearchesHeadline}
                tagStyle="bodyLargeBold"
              >
                {t('items.contentSearch.popularSearches')}
              </Typography>

              {contentSearchSettings?.data?.popularSearches.map(
                (popularSearch, popularSearchIndex) => {
                  const index = itemIndex++;

                  return (
                    <Link
                      key={popularSearchIndex}
                      ref={el => {
                        linkRefs.current[index] = el;
                      }}
                      className={classNames(styles.link, {
                        [styles.isActive]: activeIndex === index,
                      })}
                      onClick={() => handleDropdownItemClick(popularSearch)}
                      url={redirectUrl}
                    >
                      <Typography tagStyle="bodyLarge">
                        {popularSearch}
                      </Typography>
                    </Link>
                  );
                },
              )}
            </div>
          )}
        </React.Fragment>
      );
    }

    if (hasAutocompleteSuggestions) {
      return (
        <div className={styles.autocomplete}>
          {dataAutocomplete.map((autocomplete, autocompleteIndex) => {
            const index = itemIndex++;

            const highlightedSuggestion = highlightTerm(
              autocomplete,
              searchString,
              'unmatched',
              styles.highlight,
            );

            return (
              <Link
                key={autocompleteIndex}
                ref={el => {
                  linkRefs.current[index] = el;
                }}
                className={classNames(styles.link, {
                  [styles.isActive]: activeIndex === index,
                })}
                onClick={() => handleDropdownItemClick(autocomplete)}
                url={redirectUrl}
              >
                <Typography
                  dangerouslySetInnerHTML={{ __html: highlightedSuggestion }}
                  tagStyle="bodyLarge"
                />
              </Link>
            );
          })}
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isStandAlone]: isStandAlone,
      })}
    >
      <div ref={contentSearchRef} className={styles.searchContainer}>
        {renderInputField()}

        <AnimatePresence>
          {shouldShowDropdown && (
            <motion.div
              key="content-search-dropdown"
              animate="show"
              className={styles.dropdown}
              exit="hidden"
              initial="hidden"
              transition={{ duration: 0.2 }}
              variants={{
                hidden: {
                  opacity: 0,
                  translateY: -8,
                },
                show: {
                  opacity: 1,
                  translateY: 0,
                },
              }}
            >
              {renderDropdownContent()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ContentSearchBar;
